import * as React from 'react';
import { IQuotation } from 'app/shared/model/quotation.model';
import { formatDateEng } from 'app/shared/util/date-utils';
import { FileData, parseFileData } from 'app/shared/util/file-utils';
import { IIban } from 'app/shared/model/iban.model';
import { useEffect, useState } from 'react';
import { numberToArabicNumerals, numberToArabicWords, numberToWords } from 'app/shared/util/number-utils';
import { Storage } from 'react-jhipster';

type Props = {
  captureRef?: any;
  quotationEntity: IQuotation;
  ibanEntity: IIban;
  quotationLoading?: boolean;
  ibanLoading?: boolean;
};

// Updated function to render base64 data as an image or a placeholder
const renderBase64Image = (base64Data: FileData | null, altText: string) => {
  if (!base64Data?.base64) {
    return (
      <div
        style={{
          width: '48px',
          height: '48px',
          display: 'block',
          margin: '0 auto',
        }}
      />
    );
  }

  let mimeType = 'image/png';
  if (base64Data?.fileName) {
    const extension = base64Data.fileName.split('.').pop()?.toLowerCase();
    if (extension === 'gif') {
      mimeType = 'image/gif';
    } else if (extension === 'jpg' || extension === 'jpeg') {
      mimeType = 'image/jpeg';
    }
  }

  return (
    <img
      src={`data:${mimeType};base64,${base64Data.base64}`}
      alt={altText}
      style={{ maxWidth: '150px', maxHeight: '150px', display: 'block', margin: '0 auto' }}
    />
  );
};

export const LocalFormPrint = (props: Props) => {
  const { captureRef, quotationEntity, ibanEntity, quotationLoading, ibanLoading } = props;
  const companyName = `${quotationEntity?.commodityBuyer?.firstName ?? ''} ${quotationEntity?.commodityBuyer?.lastName ?? ''}`.trim();
  const [calculatedAmount, setCalculatedAmount] = useState('-');
  const [arabicAmountWords, setArabicAmountWords] = useState('صفر');
  const [amountInWords, setAmountInWords] = useState('Zero');

  useEffect(() => {
    if (!quotationLoading && !ibanLoading && quotationEntity?.amount != null) {
      const calculatedAmountNumber = quotationEntity.amount - ((quotationEntity.fee ?? 0) + (quotationEntity.commission ?? 0));
      const newCalculatedAmount = calculatedAmountNumber.toLocaleString();
      const newArabicAmountWords = numberToArabicNumerals(calculatedAmountNumber);
      const newAmountInWords = numberToWords(calculatedAmountNumber);

      setCalculatedAmount(newCalculatedAmount);
      setArabicAmountWords(newArabicAmountWords);
      setAmountInWords(newAmountInWords);
    }
  }, [quotationEntity, quotationLoading, ibanEntity, ibanLoading]);

  return (
    <div
      ref={captureRef}
      id="contentToPrint"
      style={{
        position: 'relative',
        width: '794px',
        height: '1123px',
        overflow: 'hidden',
        boxShadow: '0 0 40px rgba(0, 0, 0, 0.2)',
        backgroundColor: 'transparent',
        margin: '20px auto',
      }}
    >
      {quotationLoading || ibanLoading ? (
        <p style={{ textAlign: 'center', fontSize: '14px', margin: '20px' }}>جارٍ التحميل...</p>
      ) : (
        <>
          <div id="headerPrint" className="header-print">
            <table className="header-table">
              <tbody>
                <tr>
                  <td className="header-center">
                    <p className="header-title">امر دفع مقابل شراء بضاعة</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            className="subheader-content"
            style={{
              margin: '0 48px',
              ...(Storage.session.get('locale') === 'ar-ly'
                ? {
                    direction: 'rtl',
                    textAlign: 'right',
                    fontSize: '14px',
                    margin: '0 48px 20px',
                  }
                : {}),
            }}
          >
            <p>السادة / بنك وربة المحترمين</p>
            <p>الموضوع: تحويل داخلي خصما من حسابنا رقم / {quotationEntity?.commodityBuyer?.accountNumber || '.......'}</p>
            <p>
              يرجى التكرم بالعمل على تحويل مبلغ ({calculatedAmount} {quotationEntity?.buyCurrency || ''}) فقط {amountInWords}{' '}
              {quotationEntity?.buyCurrency || ''} لا غير
            </p>
            <p>خصما من حسابنا المذكور أعاله الي حساب رقم / {ibanEntity?.ibanNumber || '.......'}</p>
            <p>
              باسم {quotationEntity?.customer?.nameArabic || '.......'} / رقم مدني / {quotationEntity?.customer?.ic || '.......'}
            </p>
            <p></p>
            <p>
              وذلك مقابل شراء البضاعه المذكوره في طلب الشراء رقم : {quotationEntity?.id || '.......'} بتاريخ{' '}
              {formatDateEng(quotationEntity?.createdDate)} الصادر
            </p>
            <p>من قبلكم .</p>
          </div>

          <div
            className="footer-content"
            style={{
              ...(Storage.session.get('locale') === 'ar-ly'
                ? {
                    direction: 'rtl',
                    textAlign: 'right',
                    width: '100%',
                    margin: '100px 0 0',
                  }
                : {}),
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td
                    style={{
                      width: '33.33%',
                      textAlign: 'center',
                      fontSize: '14px',
                      padding: '10px',
                      verticalAlign: 'top',
                    }}
                  >
                    <p style={{ margin: '0 0 10px 0' }}>اسم الشركه</p>
                    <p style={{ margin: '0' }}>{companyName || '.......'}</p>
                  </td>
                  <td
                    style={{
                      width: '33.33%',
                      textAlign: 'center',
                      fontSize: '14px',
                      padding: '10px',
                      verticalAlign: 'top',
                    }}
                  >
                    <p style={{ margin: '0 0 10px 0' }}>ختم الشركة</p>
                    {renderBase64Image(parseFileData(quotationEntity?.commodityBuyer?.stampData), 'Company Stamp')}
                  </td>
                  <td
                    style={{
                      width: '33.33%',
                      textAlign: 'center',
                      fontSize: '14px',
                      padding: '10px',
                      verticalAlign: 'top',
                    }}
                  >
                    <p style={{ margin: '0 0 10px 0' }}>توقيع المفوض</p>
                    {renderBase64Image(parseFileData(quotationEntity?.commodityBuyer?.signatureData), 'Authorized Signature')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
